export interface ExperienceFlagRegistration {
  capability: 'experience-brand' | 'experience-program-routing';
  deployFlag: 'experience-v1';
  cohortFlag: 'experience_brand_v1' | 'experience_program_routing_v1';
  owner: string;
  expiresAt: string;
  legacyDestination: string;
}

export const EXPERIENCE_FLAG_CATALOGUE = [
  {
    capability: 'experience-brand',
    deployFlag: 'experience-v1',
    cohortFlag: 'experience_brand_v1',
    owner: 'Platform Experience',
    expiresAt: '2027-02-28',
    legacyDestination: 'current certified Statura identity targets',
  },
  {
    capability: 'experience-program-routing',
    deployFlag: 'experience-v1',
    cohortFlag: 'experience_program_routing_v1',
    owner: 'Platform Security',
    expiresAt: '2027-02-28',
    legacyDestination: 'existing module/RBAC route guards plus fail-closed program boundary',
  },
] as const satisfies readonly ExperienceFlagRegistration[];

export const EXPERIENCE_PLANNED_FLAG_CATALOGUE = [
  {
    capability: 'experience-shell',
    deployFlag: 'experience-v1',
    cohortFlag: 'experience_shell_v1',
    owner: 'Platform Experience',
    expiresAt: '2027-02-28',
    legacyDestination: 'current dashboard shell composition',
    status: 'PLANNED_NO_REVERSIBLE_CONSUMER',
    activationRequirement:
      'Define and certify a reversible candidate shell composition before moving this registration into the active catalogue.',
  },
] as const;

export interface ExperienceRolloutInput {
  authorityAllowed: boolean | null | undefined;
  programAllowed: boolean | null | undefined;
  deployFlagEnabled: boolean | null | undefined;
  cohortFlagEnabled: boolean | null | undefined;
}

export type ExperienceRolloutDecision =
  | { enabled: true; reason: 'all_controls_allow' }
  | {
      enabled: false;
      reason:
        | 'authority_refused'
        | 'program_refused'
        | 'deploy_flag_off_or_unknown'
        | 'cohort_flag_off_or_unknown';
    };

export const EXPERIENCE_BRAND_CUTOVER_EVIDENCE = {
  status: 'CUTOVER_BLOCKED',
  blockedReason: 'brand_packet_incomplete',
  canonicalBrandPacketDigestSha256: null,
  candidateRevision: null,
  claudeDirectionReview: 'CONDITIONAL',
  claudeRenderedReviewArtifacts: null,
  accountableOwnerApprovalArtifact: null,
  authority:
    'docs/design/site-wide-experience-architecture-2026-08-23/09-brand-identity-and-evidence-architecture.md',
} as const;

export interface ExperienceBrandRolloutInput extends ExperienceRolloutInput {
  environment: 'development' | 'test' | 'production' | null | undefined;
}

export interface ExperienceBrandCutoverArtifactReference {
  path: string;
  sha256: string;
}

export interface ExperienceBrandCutoverEvidence {
  status: 'CUTOVER_BLOCKED' | 'EVIDENCE_BOUND_PENDING_VERIFICATION';
  blockedReason: Exclude<
    ExperienceBrandCutoverEvidenceDecision['reason'],
    'all_cutover_evidence_approved'
  >;
  canonicalBrandPacketDigestSha256: string | null;
  candidateRevision: string | null;
  claudeDirectionReview: 'APPROVED' | 'CONDITIONAL' | 'NOT_APPROVED';
  claudeRenderedReviewArtifacts: {
    request: ExperienceBrandCutoverArtifactReference;
    rawOutput: ExperienceBrandCutoverArtifactReference;
    provenance: ExperienceBrandCutoverArtifactReference;
  } | null;
  accountableOwnerApprovalArtifact: ExperienceBrandCutoverArtifactReference | null;
}

export type ExperienceBrandRolloutDecision =
  | ExperienceRolloutDecision
  | {
      enabled: false;
      reason: Exclude<
        ExperienceBrandCutoverEvidenceDecision['reason'],
        'all_cutover_evidence_approved'
      >;
    };

export type ExperienceBrandRenderMode = 'legacy' | 'candidate';

export type ExperienceBrandCutoverEvidenceDecision =
  | { approved: true; reason: 'all_cutover_evidence_approved' }
  | {
      approved: false;
      reason:
        | 'brand_packet_incomplete'
        | 'brand_packet_not_revision_bound'
        | 'claude_direction_not_approved'
        | 'claude_rendered_review_artifacts_missing'
        | 'accountable_owner_not_approved'
        | 'cutover_artifacts_not_verified';
    };

export function resolveExperienceRollout(input: ExperienceRolloutInput): ExperienceRolloutDecision {
  if (input.authorityAllowed !== true) return { enabled: false, reason: 'authority_refused' };
  if (input.programAllowed !== true) return { enabled: false, reason: 'program_refused' };
  if (input.deployFlagEnabled !== true) {
    return { enabled: false, reason: 'deploy_flag_off_or_unknown' };
  }
  if (input.cohortFlagEnabled !== true) {
    return { enabled: false, reason: 'cohort_flag_off_or_unknown' };
  }
  return { enabled: true, reason: 'all_controls_allow' };
}

/**
 * Runtime evaluation intentionally accepts no caller-supplied evidence. The
 * checked-in authority remains blocked until the filesystem/git verifier has
 * bound every artifact and a later reviewed release changes that authority.
 */
export function evaluateExperienceBrandCutoverEvidence(): ExperienceBrandCutoverEvidenceDecision {
  const evidence: ExperienceBrandCutoverEvidence = EXPERIENCE_BRAND_CUTOVER_EVIDENCE;
  if (evidence.status === 'CUTOVER_BLOCKED') {
    return { approved: false, reason: evidence.blockedReason };
  }
  if (
    !/^[a-f0-9]{64}$/u.test(evidence.canonicalBrandPacketDigestSha256 ?? '') ||
    !/^[a-f0-9]{40}$/u.test(evidence.candidateRevision ?? '')
  ) {
    return { approved: false, reason: 'brand_packet_not_revision_bound' };
  }
  if (evidence.claudeDirectionReview !== 'APPROVED') {
    return { approved: false, reason: 'claude_direction_not_approved' };
  }
  if (!evidence.claudeRenderedReviewArtifacts) {
    return { approved: false, reason: 'claude_rendered_review_artifacts_missing' };
  }
  if (!evidence.accountableOwnerApprovalArtifact) {
    return { approved: false, reason: 'accountable_owner_not_approved' };
  }
  return { approved: false, reason: 'cutover_artifacts_not_verified' };
}

/**
 * Candidate brand rendering is available for bounded local evidence only.
 * Production additionally requires the complete rendered-evidence approval
 * chain; the current source-only Claude verdict is deliberately ineligible.
 */
export function resolveExperienceBrandRollout(
  input: ExperienceBrandRolloutInput,
): ExperienceBrandRolloutDecision {
  const controls = resolveExperienceRollout(input);
  if (!controls.enabled) return controls;
  if (input.environment === 'development' || input.environment === 'test') return controls;

  const cutover = evaluateExperienceBrandCutoverEvidence();
  if (cutover.approved === false) return { enabled: false, reason: cutover.reason };
  return controls;
}

export function resolveExperienceBrandRenderMode(input: {
  candidateControlsAllowed: boolean;
  environment: 'development' | 'test' | 'production' | null | undefined;
}): ExperienceBrandRenderMode {
  return resolveExperienceBrandRollout({
    authorityAllowed: input.candidateControlsAllowed,
    programAllowed: input.candidateControlsAllowed,
    deployFlagEnabled: input.candidateControlsAllowed,
    cohortFlagEnabled: input.candidateControlsAllowed,
    environment: input.environment,
  }).enabled
    ? 'candidate'
    : 'legacy';
}
